/**
 * Content Recycling API Service
 * 
 * Service for connecting to the backend content recycling endpoints implemented in PX-106.
 * Handles intelligent content reuse, variation generation, and recycling opportunities.
 */

import { createApiService, responseTransformers } from './apiUtils';

// Create specialized API service for content recycling
const contentRecyclingApiService = createApiService('ContentRecycling', '/api/analytics/recycling');

/**
 * Transform backend recycling data to frontend format
 * @param {Object} backendData - Recycling data from backend
 * @returns {Object} Transformed recycling data for frontend
 */
const transformRecyclingData = (backendData) => {
  if (!backendData) return null;

  const camelCased = responseTransformers.toCamelCase(backendData);
  
  return {
    id: camelCased.id,
    originalPost: camelCased.originalPost ? {
      id: camelCased.originalPost.id,
      content: camelCased.originalPost.content,
      platform: camelCased.originalPost.platform,
      createdAt: camelCased.originalPost.createdAt,
      publishedAt: camelCased.originalPost.publishedAt
    } : null,
    contentBucket: camelCased.contentBucket ? {
      id: camelCased.contentBucket.id,
      name: camelCased.contentBucket.name,
      isEvergreen: camelCased.contentBucket.isEvergreen
    } : null,
    performanceScore: camelCased.performanceScore || 0,
    peakEngagementRate: camelCased.peakEngagementRate || 0,
    totalReach: camelCased.totalReach || 0,
    timesRecycled: camelCased.timesRecycled || 0,
    lastRecycledAt: camelCased.lastRecycledAt,
    nextEligibleDate: camelCased.nextEligibleDate,
    suggestedVariations: camelCased.suggestedVariations || [],
    isEligible: camelCased.isEligible || false,
    recyclingScore: camelCased.recyclingScore || 0
  };
};

/**
 * Transform backend variation data to frontend format
 * @param {Object} backendVariation - Variation data from backend
 * @returns {Object} Transformed variation data for frontend
 */
const transformVariationData = (backendVariation) => {
  if (!backendVariation) return null;

  const camelCased = responseTransformers.toCamelCase(backendVariation);
  
  return {
    id: camelCased.id,
    recyclableContent: camelCased.recyclableContent,
    variationType: camelCased.variationType,
    variationContent: camelCased.variationContent,
    targetPlatform: camelCased.targetPlatform,
    confidenceScore: camelCased.confidenceScore || 0,
    isApproved: camelCased.isApproved || false,
    createdAt: camelCased.createdAt,
    approvedAt: camelCased.approvedAt,
    generatedBy: camelCased.generatedBy || 'ai',
    metadata: camelCased.metadata || {}
  };
};

/**
 * Content Recycling API functions
 */
export const contentRecyclingApi = {
  /**
   * Get recycling opportunities for user
   * @param {Object} options - Query options
   * @returns {Promise} Recycling opportunities
   */
  getOpportunities: async (options = {}) => {
    console.log('[Content Recycling API] Fetching recycling opportunities:', options);
    
    const response = await contentRecyclingApiService.get('/opportunities/', {
      content_bucket: options.contentBucketId,
      platform: options.platform,
      min_performance_score: options.minPerformanceScore,
      limit: options.limit,
    });
    
    return responseTransformers.single(response, (data) => 
      (data.opportunities || data.results || []).map(transformRecyclingData)
    );
  },

  /**
   * Get recycling dashboard overview
   * @returns {Promise} Dashboard data
   */
  getDashboard: async () => {
    console.log('[Content Recycling API] Fetching recycling dashboard');
    
    const response = await contentRecyclingApiService.get('/dashboard/');
    return responseTransformers.single(response, (data) => {
      const camelCased = responseTransformers.toCamelCase(data);
      return {
        totalOpportunities: camelCased.totalOpportunities || 0,
        readyToRecycle: camelCased.readyToRecycle || 0,
        totalRecycled: camelCased.totalRecycled || 0,
        averagePerformanceBoost: camelCased.averagePerformanceBoost || 0,
        topPerformingBuckets: camelCased.topPerformingBuckets || [],
        recentRecycling: camelCased.recentRecycling || [],
        upcomingOpportunities: camelCased.upcomingOpportunities || []
      };
    });
  },

  /**
   * Get user's recyclable content
   * @param {Object} options - Query options
   * @returns {Promise} Recyclable content list
   */
  getRecyclableContent: async (options = {}) => {
    console.log('[Content Recycling API] Fetching recyclable content:', options);
    
    const response = await contentRecyclingApiService.get('/content/', {
      bucket: options.bucketId,
      platform: options.platform,
      status: options.status,
      page: options.page,
      page_size: options.pageSize,
    });
    
    return responseTransformers.paginated(response, transformRecyclingData);
  },

  /**
   * Create recyclable content entry
   * @param {Object} contentData - Content data to make recyclable
   * @returns {Promise} Created recyclable content
   */
  createRecyclableContent: async (contentData) => {
    console.log('[Content Recycling API] Creating recyclable content:', contentData);
    
    const response = await contentRecyclingApiService.post('/create/', {
      post_id: contentData.postId,
      content_bucket_id: contentData.contentBucketId,
      performance_threshold: contentData.performanceThreshold || 0.7,
      recycling_interval_days: contentData.recyclingIntervalDays || 90
    });
    
    return responseTransformers.single(response, transformRecyclingData);
  },

  /**
   * Generate content variations
   * @param {string} recyclableContentId - ID of recyclable content
   * @param {Object} options - Generation options
   * @returns {Promise} Generated variations
   */
  generateVariations: async (recyclableContentId, options = {}) => {
    console.log('[Content Recycling API] Generating variations for:', recyclableContentId, options);
    
    const response = await contentRecyclingApiService.post('/generate-variations/', {
      recyclable_content_id: recyclableContentId,
      target_platforms: options.targetPlatforms || [],
      variation_types: options.variationTypes || ['tone_shift', 'format_change', 'platform_optimization'],
      max_variations: options.maxVariations || 5
    });
    
    return responseTransformers.single(response, (data) => 
      (data.variations || []).map(transformVariationData)
    );
  },

  /**
   * Get variations for recyclable content
   * @param {string} recyclableContentId - ID of recyclable content
   * @returns {Promise} Content variations
   */
  getVariations: async (recyclableContentId) => {
    console.log('[Content Recycling API] Fetching variations for:', recyclableContentId);
    
    const response = await contentRecyclingApiService.get(`/content/${recyclableContentId}/variations/`);
    return responseTransformers.single(response, (data) => 
      (data.variations || []).map(transformVariationData)
    );
  },

  /**
   * Approve a content variation
   * @param {string} variationId - ID of variation to approve
   * @returns {Promise} Approval result
   */
  approveVariation: async (variationId) => {
    console.log('[Content Recycling API] Approving variation:', variationId);
    
    const response = await contentRecyclingApiService.post(`/variations/${variationId}/approve/`);
    return responseTransformers.single(response, transformVariationData);
  },

  /**
   * Reject a content variation
   * @param {string} variationId - ID of variation to reject
   * @param {string} reason - Rejection reason (optional)
   * @returns {Promise} Rejection result
   */
  rejectVariation: async (variationId, reason = '') => {
    console.log('[Content Recycling API] Rejecting variation:', variationId, reason);
    
    const response = await contentRecyclingApiService.post(`/variations/${variationId}/reject/`, { reason });
    return responseTransformers.single(response);
  },

  /**
   * Schedule recycled content for posting
   * @param {string} variationId - ID of approved variation
   * @param {Object} scheduleData - Scheduling information
   * @returns {Promise} Scheduling result
   */
  scheduleRecycledContent: async (variationId, scheduleData) => {
    console.log('[Content Recycling API] Scheduling recycled content:', variationId, scheduleData);
    
    const response = await contentRecyclingApiService.post(`/variations/${variationId}/schedule/`, {
      scheduled_for: scheduleData.scheduledFor,
      platforms: scheduleData.platforms || [],
      auto_post: scheduleData.autoPost || false
    });
    
    return responseTransformers.single(response);
  },

  /**
   * Get recycling performance analytics
   * @param {Object} options - Analytics options
   * @returns {Promise} Performance analytics
   */
  getRecyclingAnalytics: async (options = {}) => {
    console.log('[Content Recycling API] Fetching recycling analytics:', options);
    
    const response = await contentRecyclingApiService.get('/analytics/', {
      time_range: options.timeRange,
      content_bucket: options.contentBucketId,
    });
    
    return responseTransformers.single(response);
  }
};

/**
 * Content recycling tracking helper function
 * @param {string} eventName - Event name
 * @param {Object} properties - Event properties
 */
export const trackRecyclingEvent = (eventName, properties = {}) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Recycling Tracking] ${eventName}:`, properties);
  }
  
  // Track event with analytics API
  if (window.analytics && typeof window.analytics.track === 'function') {
    window.analytics.track(`content_recycling_${eventName}`, properties);
  }
};

export default contentRecyclingApi; 